import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  EmptyState,
  Input,
  LoadingState,
  SectionHeader,
  Select,
  StatusChip,
  Tabs,
  Textarea,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createDeskApi, REASONS, RESTRICT_HOUR_PRESETS, SUBJECT_LABEL, SUSPEND_DAY_PRESETS, type DecisionAction, type ReportContext } from "./deskApi";
import { createStaffApi } from "@/modules/staff/api";

const LADDER: DecisionAction[] = ["warn", "restrict_user", "suspend_user", "ban_user"];
import { fmtDate, PersonChip, personName, priorityTone, SlaChip, SubjectRender, timeAgo } from "./DeskShared";
import styles from "./desk.module.css";

const OPEN = ["submitted", "triaged", "linked_or_opened"];

const ACTION_INFO: Record<DecisionAction, { label: string; help: string; danger?: boolean }> = {
  remove_content: { label: "Remove content", help: "Hidden from everyone in Play. Reversible." },
  warn: { label: "Warn the author", help: "A warning in their inbox and account record." },
  restrict_user: { label: "Restrict account (1 to 48 hours)", help: "Can still browse, book, pay, react, join and reply in existing chats and communities. Can't post, comment, publish Highlights or Replays, create or rename communities, or start new DMs. Ends by itself." },
  suspend_user: { label: "Suspend account (1 to 30 days)", help: "The app is blocked. They see the reason, the end date and how to appeal.", danger: true },
  ban_user: { label: "Ban permanently", help: "Super admin only. Takes effect at once. They can still appeal.", danger: true },
  remove_community: { label: "Take down community", help: "Archived and hidden from discovery. Reversible.", danger: true },
};

/**
 * One report, worked end to end: the reported content as the reporter saw it
 * (private media via a 5-minute signed link), who reported it and who posted
 * it, everyone else who reported the same thing, the author's record, and the
 * decision — which opens the case, records the enforcement, removes the
 * content, and tells the people involved in Play.
 */
export function ReportCasePage() {
  const { reportId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createDeskApi(client), [client]);
  const toast = useToast();
  const [ctx, setCtx] = useState<ReportContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuper, setIsSuper] = useState(false);
  useEffect(() => {
    createStaffApi(client).me().then((m) => setIsSuper(m.tier === "super_admin")).catch(() => setIsSuper(false));
  }, [client]);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Trust & Safety", to: "/app/trust-safety?tab=queue" },
    { label: ctx ? `${SUBJECT_LABEL[ctx.report.subject_type] ?? "Report"} report` : "Report" },
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setCtx(await api.reportContext(reportId)); setError(null); }
    catch (e) { setError(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [api, reportId]);
  useEffect(() => void load(), [load]);

  if (loading && !ctx) return <LoadingState message="Loading report…" />;
  if (error || !ctx) {
    return <EmptyState title="Report unavailable" description={error ?? "Not found or not authorized."} reason="scope"
      action={<Button variant="secondary" onClick={() => void load()}>Retry</Button>} />;
  }
  const r = ctx.report;
  const open = OPEN.includes(r.status) && r.resolution !== "escalated";
  const escalatedOpen = OPEN.includes(r.status) && r.resolution === "escalated";

  return (
    <Stack gap={16}>
      <SectionHeader
        title={`${SUBJECT_LABEL[r.subject_type] ?? r.subject_type} reported for ${r.reason_label ?? "an unspecified reason"}`}
        description={`Reported ${fmtDate(r.submitted_at)} (${timeAgo(r.submitted_at)})${r.source === "auto_blocklist" ? " by the blocklist filter" : r.source === "staff" ? " by staff" : ""}.`}
        actions={<Flex gap={8}>
          <Button variant="secondary" onClick={() => void navigate("/app/trust-safety?tab=queue")}>Back to queue</Button>
          <Button variant="secondary" onClick={() => void load()}>Refresh</Button>
        </Flex>}
      />
      <Flex gap={8} wrap>
        <StatusChip tone={priorityTone(r.priority)}>{r.priority} priority</StatusChip>
        {open ? <SlaChip state={r.sla_state} dueAt={r.sla_due_at} /> : null}
        <StatusChip tone={open ? "info" : r.resolution === "actioned" ? "success" : r.resolution === "escalated" ? "warning" : "neutral"}>
          {open ? "Open" : r.resolution ?? r.status}
        </StatusChip>
        {r.moderation_case_id ? <Link to={`/app/trust-safety/cases/${r.moderation_case_id}`}><StatusChip tone="neutral">Case</StatusChip></Link> : null}
        {ctx.other_reports.length > 0 ? <StatusChip tone="warning">{ctx.other_reports.length + 1} reports on this</StatusChip> : null}
      </Flex>

      <div className={styles.twoCol}>
        <Stack gap={16}>
          <SubjectRender subject={ctx.subject} snapshot={r.subject_snapshot} />
          <Tabs items={[
            { id: "reports", label: `Reports (${ctx.other_reports.length + 1})`, content: <ReportsList ctx={ctx} /> },
            { id: "history", label: "Author's record", content: <History ctx={ctx} /> },
            { id: "notes", label: `Notes (${ctx.internal_notes.length})`, content: <Notes ctx={ctx} /> },
            { id: "audit", label: "Audit trail", content: <Audit ctx={ctx} /> },
          ]} />
        </Stack>
        <div className={styles.sticky}>
          <Stack gap={12}>
            <div className={styles.panel}>
              <Text role="label" as="div">Reported party</Text>
              {ctx.reported_user ? <PersonChip person={ctx.reported_user} /> : <Text role="small" color="muted" as="p">Not an account (or could not be resolved).</Text>}
              {ctx.history ? (
                <Text role="small" color="secondary" as="p">
                  {ctx.history.reports_total} other reports · {ctx.history.reports_actioned} actioned · {ctx.history.takedowns_total} takedowns
                  {ctx.history.current_restriction ? ` · currently ${ctx.history.current_restriction}` : ""}
                </Text>
              ) : null}
              <Text role="label" as="div">Reporter</Text>
              {r.reporter ? <PersonChip person={r.reporter} /> : <Text role="small" color="muted" as="p">{r.source === "auto_blocklist" ? "Automatic filter" : "Unknown"}</Text>}
              {ctx.reporter_track_record && ctx.reporter_track_record.total > 1 ? (
                <Text role="caption" color="muted" as="p">Has filed {ctx.reporter_track_record.total} reports; {ctx.reporter_track_record.dismissed} dismissed. Never shown to the reported party.</Text>
              ) : null}
              {r.evidence_reference ? <div className={styles.note}><Text role="caption" color="secondary" as="div">What the reporter said</Text><Text role="small" as="p">{r.evidence_reference}</Text></div> : null}
            </div>
            {open || escalatedOpen ? (
              <>
                <AssignPanel reportId={r.id} assignedTo={r.assigned_to_user_id ?? null} api={api} onDone={async (msg) => { toast.push({ title: msg, tone: "success" }); await load(); }} />
                <DecisionPanel ctx={ctx} escalated={escalatedOpen} isSuper={isSuper} onDone={async (msg) => { toast.push({ title: msg, tone: "success" }); await load(); }} api={api} />
              </>
            ) : (
              <div className={styles.panel}>
                <Text role="title" as="h3">Decided</Text>
                <Text role="small" as="p">{r.resolution === "actioned" ? "Action was taken" : r.resolution === "dismissed" ? "Dismissed — no violation" : r.status} {r.resolved_at ? `on ${fmtDate(r.resolved_at)}` : ""}.</Text>
                {ctx.takedowns.some((t) => !t.restored_at) ? (
                  <Text role="small" color="secondary" as="p">To undo a removal, use Restore in the Takedowns tab.</Text>
                ) : null}
                <Button variant="secondary" onClick={() => void navigate("/app/trust-safety?tab=takedowns")}>Takedowns</Button>
              </div>
            )}
          </Stack>
        </div>
      </div>
    </Stack>
  );
}

/**
 * Who works this report. Only people who see the whole queue can assign; for
 * an Employee who sees only their own reports the list call is refused and the
 * panel stays hidden.
 */
function AssignPanel({ reportId, assignedTo, api, onDone }: { reportId: string; assignedTo: string | null; api: ReturnType<typeof createDeskApi>; onDone: (msg: string) => Promise<void> }) {
  const [people, setPeople] = useState<Array<{ id: string; display_name: string | null }> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    api.assignees().then((r) => setPeople(r.assignees)).catch(() => setPeople(null));
  }, [api]);
  if (!people) return null;
  const change = async (value: string) => {
    setBusy(true); setErr(null);
    try {
      await api.assign(reportId, value || null);
      await onDone(value ? "Report assigned" : "Report returned to the shared queue");
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className={styles.panel}>
      <Select
        label="Assigned to"
        value={assignedTo ?? ""}
        disabled={busy}
        onChange={(e) => void change(e.target.value)}
        options={[{ value: "", label: "Nobody (shared queue)" }, ...people.map((p) => ({ value: p.id, label: p.display_name ?? p.id.slice(0, 8) }))]}
      />
      <Text role="caption" color="muted" as="p">An Employee sees only the reports assigned to them.</Text>
      {err ? <Alert tone="danger" title="Not assigned">{err}</Alert> : null}
    </div>
  );
}

function DecisionPanel({ ctx, escalated, isSuper, api, onDone }: { ctx: ReportContext; escalated: boolean; isSuper: boolean; api: ReturnType<typeof createDeskApi>; onDone: (msg: string) => Promise<void> }) {
  const r = ctx.report;
  const t = r.subject_type;
  const contentKinds = ["post", "comment", "video", "replay", "chat_message"];
  const available: DecisionAction[] = [
    ...(contentKinds.includes(t) && ctx.subject.exists && !ctx.subject.removed ? (["remove_content"] as DecisionAction[]) : []),
    ...(t === "community" && ctx.subject.exists && !ctx.subject.removed ? (["remove_community"] as DecisionAction[]) : []),
    ...(ctx.reported_user ? (["warn", "restrict_user", "suspend_user", ...(isSuper ? ["ban_user"] : [])] as DecisionAction[]) : []),
  ];
  const [outcome, setOutcome] = useState<"action" | "dismiss" | "escalate">("action");
  const [actions, setActions] = useState<DecisionAction[]>(available.includes("remove_content") ? ["remove_content"] : []);
  const [reason, setReason] = useState(r.reason_code && r.reason_code !== "blocklist" ? r.reason_code : "other");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [days, setDays] = useState("3");
  const [hours, setHours] = useState("24");
  const [ticketId, setTicketId] = useState("");
  const [related, setRelated] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // One account action at a time: warning, restriction, suspension or ban.
  const toggle = (a: DecisionAction) =>
    setActions((cur) =>
      cur.includes(a)
        ? cur.filter((x) => x !== a)
        : LADDER.includes(a)
          ? [...cur.filter((x) => !LADDER.includes(x)), a]
          : [...cur, a],
    );
  const daysNum = Number.parseInt(days, 10);
  const hoursNum = Number.parseInt(hours, 10);
  const ticketOk = ticketId.trim() === "" || /^[0-9a-f-]{36}$/i.test(ticketId.trim());
  const valid =
    note.trim().length >= 3 &&
    ticketOk &&
    (outcome !== "action" || actions.length > 0) &&
    (!actions.includes("suspend_user") || (daysNum >= 1 && daysNum <= 30)) &&
    (!actions.includes("restrict_user") || (hoursNum >= 1 && hoursNum <= 48));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.decide(r.id, {
        outcome,
        actions: outcome === "action" ? actions : [],
        reason_code: reason,
        note: note.trim(),
        ...(message.trim() ? { message_to_user: message.trim() } : {}),
        ...(outcome === "action" && actions.includes("suspend_user") ? { duration_days: daysNum } : {}),
        ...(outcome === "action" && actions.includes("restrict_user") ? { duration_hours: hoursNum } : {}),
        ...(ticketId.trim() ? { ticket_id: ticketId.trim() } : {}),
        resolve_related: related,
      });
      await onDone(outcome === "dismiss" ? "Report dismissed" : outcome === "escalate" ? "Escalated for senior review" : "Decision applied");
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className={styles.panel}>
      <Text role="title" as="h3">{escalated ? "Escalated — senior decision" : "Decision"}</Text>
      <div className={styles.actionGrid}>
        {(["action", "dismiss", "escalate"] as const).filter((o) => !(escalated && o === "escalate")).map((o) => (
          <button key={o} type="button" className={`${styles.choice} ${outcome === o ? styles.choiceOn : ""}`} onClick={() => setOutcome(o)}>
            <Text role="bodyStrong" as="div">{o === "action" ? "Take action" : o === "dismiss" ? "No violation" : "Escalate"}</Text>
            <Text role="caption" color="muted" as="div">{o === "action" ? "Pick what to do below" : o === "dismiss" ? "Reporters are told; author is not" : "Senior review, nothing changes yet"}</Text>
          </button>
        ))}
      </div>
      {outcome === "action" ? (
        available.length === 0 ? (
          <Alert tone="warning">Nothing can be acted on: the content is gone or already removed and no account could be identified. Dismiss, or escalate.</Alert>
        ) : (
          <Stack gap={8}>
            {available.map((a) => (
              <button key={a} type="button" className={`${styles.choice} ${ACTION_INFO[a].danger ? styles.choiceDanger : ""} ${actions.includes(a) ? styles.choiceOn : ""}`} onClick={() => toggle(a)} aria-pressed={actions.includes(a)}>
                <Text role="bodyStrong" as="div">{actions.includes(a) ? "✓ " : ""}{ACTION_INFO[a].label}</Text>
                <Text role="caption" color="muted" as="div">{ACTION_INFO[a].help}</Text>
              </button>
            ))}
            {actions.includes("restrict_user") ? (
              <Stack gap={4}>
                <Flex gap={8} wrap>
                  {RESTRICT_HOUR_PRESETS.map((h) => (
                    <Button key={h} variant={hours === String(h) ? "primary" : "secondary"} onClick={() => setHours(String(h))}>{h} {h === 1 ? "hour" : "hours"}</Button>
                  ))}
                </Flex>
                <Input label="Hours (1 to 48)" type="number" min={1} max={48} value={hours} onChange={(e) => setHours(e.target.value)} />
              </Stack>
            ) : null}
            {actions.includes("suspend_user") ? (
              <Stack gap={4}>
                <Flex gap={8} wrap>
                  {SUSPEND_DAY_PRESETS.map((d) => (
                    <Button key={d} variant={days === String(d) ? "primary" : "secondary"} onClick={() => setDays(String(d))}>{d} {d === 1 ? "day" : "days"}</Button>
                  ))}
                </Flex>
                <Input label="Days (1 to 30)" type="number" min={1} max={30} value={days} onChange={(e) => setDays(e.target.value)} />
              </Stack>
            ) : null}
            {!isSuper && ctx.reported_user ? (
              <Text role="caption" color="muted" as="p">A permanent ban is the Super admin&apos;s decision. Escalate if you think one is needed.</Text>
            ) : null}
          </Stack>
        )
      ) : null}
      <Select label="Policy reason" value={reason} onChange={(e) => setReason(e.target.value)} options={REASONS.filter((x) => x.value !== "blocklist")} />
      {outcome === "action" ? (
        <Textarea label="Message to the author (optional)" hint="Added to what they see in Play. Never reveals who reported them." value={message} onChange={(e) => setMessage(e.target.value)} rows={2} maxLength={1000} />
      ) : null}
      {outcome === "action" ? (
        <Input label="Linked support ticket ID (optional)" value={ticketId} onChange={(e) => setTicketId(e.target.value)} hint={ticketOk ? "Kept on the record next to this report." : "That is not a ticket ID."} />
      ) : null}
      <Textarea label="Internal note (required)" hint="Why. Kept on the case and in the audit trail; staff only." value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
      {ctx.other_reports.some((o) => OPEN.includes(o.status)) ? (
        <Checkbox label={`Also resolve the ${ctx.other_reports.filter((o) => OPEN.includes(o.status)).length} other open report(s) on this`} checked={related} onChange={(e) => setRelated(e.currentTarget.checked)} />
      ) : null}
      {err ? <Alert tone="danger" title="Not applied">{err}</Alert> : null}
      <Button variant={outcome === "action" && actions.some((a) => ACTION_INFO[a].danger) ? "danger" : "primary"} loading={busy} disabled={!valid} onClick={() => void submit()}>
        {outcome === "dismiss" ? "Dismiss report" : outcome === "escalate" ? "Escalate" : `Apply ${actions.length} action${actions.length === 1 ? "" : "s"}`}
      </Button>
    </div>
  );
}

function ReportsList({ ctx }: { ctx: ReportContext }) {
  const all = [
    { id: ctx.report.id, reporter: ctx.report.reporter, reason_label: ctx.report.reason_label, submitted_at: ctx.report.submitted_at, status: ctx.report.status, resolution: ctx.report.resolution, source: ctx.report.source, note: ctx.report.evidence_reference, current: true },
    ...ctx.other_reports.map((o) => ({ ...o, current: false })),
  ];
  return (
    <div className={styles.timeline}>
      {all.map((o) => (
        <div key={o.id} className={styles.timelineItem}>
          <Text role="caption" color="muted" as="span">{fmtDate(o.submitted_at)}</Text>
          <div>
            <Text role="small" as="div">
              {o.source === "auto_blocklist" ? "Blocklist filter" : personName(o.reporter)} — {o.reason_label ?? "no reason"}
              {o.current ? " (this report)" : ""} · {o.resolution ?? o.status}
            </Text>
            {o.note ? <Text role="caption" color="secondary" as="div">“{o.note}”</Text> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function History({ ctx }: { ctx: ReportContext }) {
  const h = ctx.history;
  if (!h) return <Text role="small" color="muted" as="p">No account is attached to this subject.</Text>;
  return (
    <Stack gap={12}>
      <Flex gap={16} wrap>
        <Text role="small" as="span">{h.reports_total} other reports ({h.reports_last_30d} in 30 days)</Text>
        <Text role="small" as="span">{h.reports_actioned} actioned · {h.reports_dismissed} dismissed · {h.reports_open} open</Text>
        <Text role="small" as="span">Standing: {h.current_restriction ?? "good"}</Text>
      </Flex>
      <Text role="label" as="div">Enforcements</Text>
      {h.enforcements.length === 0 ? <Text role="small" color="muted" as="p">None.</Text> : (
        <div className={styles.timeline}>
          {h.enforcements.map((e) => (
            <div key={String(e.id)} className={styles.timelineItem}>
              <Text role="caption" color="muted" as="span">{fmtDate(String(e.effective_from))}</Text>
              <Text role="small" as="span">{String(e.action_type).replace(/_/g, " ")} · {String(e.status)}{e.effective_to ? ` · until ${fmtDate(String(e.effective_to))}` : ""}</Text>
            </div>
          ))}
        </div>
      )}
      <Text role="label" as="div">Takedowns</Text>
      {h.takedowns.length === 0 ? <Text role="small" color="muted" as="p">None.</Text> : (
        <div className={styles.timeline}>
          {h.takedowns.map((t) => (
            <div key={String(t.id)} className={styles.timelineItem}>
              <Text role="caption" color="muted" as="span">{fmtDate(String(t.removed_at))}</Text>
              <Text role="small" as="span">{SUBJECT_LABEL[String(t.subject_type)] ?? String(t.subject_type)} · {String(t.reason_code)}{t.restored_at ? " · restored" : ""}</Text>
            </div>
          ))}
        </div>
      )}
    </Stack>
  );
}

function Notes({ ctx }: { ctx: ReportContext }) {
  if (ctx.internal_notes.length === 0) return <Text role="small" color="muted" as="p">No notes yet. The note you write with a decision is saved here.</Text>;
  return (
    <Stack gap={8}>
      {ctx.internal_notes.map((n) => (
        <div key={n.id} className={styles.note}>
          <Text role="caption" color="secondary" as="div">{personName(n.author)} · {fmtDate(n.created_at)} · internal</Text>
          <Text role="small" as="p">{n.body}</Text>
        </div>
      ))}
    </Stack>
  );
}

function Audit({ ctx }: { ctx: ReportContext }) {
  if (ctx.audit_trail.length === 0) return <Text role="small" color="muted" as="p">No recorded actions yet.</Text>;
  return (
    <div className={styles.timeline}>
      {ctx.audit_trail.map((a) => (
        <div key={a.id} className={styles.timelineItem}>
          <Text role="caption" color="muted" as="span">{fmtDate(a.occurred_at)}</Text>
          <Text role="small" as="span">{a.action} — {a.actor ? personName(a.actor) : "system"}{a.reason ? ` · ${a.reason}` : ""}</Text>
        </div>
      ))}
    </div>
  );
}
